import { useQuery } from '@tanstack/react-query';
import { customerAPI, type CustomerBranchLinks, type SubsidiaryBranchOption } from '@matix/api-client';
import type {
  Area,
  Customer,
  CustomerSearchResponse,
  CustomerType,
  Governorate,
  VendorCategory,
  Country,
} from '@matix/types';

/**
 * Customer query hooks for the picker modal.
 *
 * Search:
 *   - Debounce in the caller (the modal). `q` is what the user has typed
 *     after debounce, not the raw input value.
 *   - Empty `q` returns the most recent N — useful for the initial picker
 *     view ("here's who you usually see") without making the cashier type
 *     anything to find someone they know.
 *   - Different `type` (cash vs credit) hits different sources, so cache key
 *     includes type.
 *   - `staleTime: 30s` — customers don't change often during a shift.
 *
 * Detail:
 *   - Loaded only when a customer is selected (enabled flag).
 *   - Full stats — visits, total spent, last visit, favorite item.
 *   - Stats are computed live from Inv_Receipt_h, so we don't keep them
 *     stale for long (60s).
 */

const STALE_SEARCH_MS = 30 * 1000;
const STALE_DETAIL_MS = 60 * 1000;

export function useCustomerSearch(
  q: string,
  type: CustomerType,
  limit = 20,
): { data: CustomerSearchResponse | undefined; isLoading: boolean; error: unknown } {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', 'search', type, q, limit],
    queryFn: () => customerAPI.search(q, type, limit),
    staleTime: STALE_SEARCH_MS,
    // We DO want to fetch with empty q (it returns "most recent"), so no
    // enabled flag here.
  });
  return { data, isLoading, error };
}

export function useCustomerDetail(
  id: number | null,
  type: CustomerType,
): { data: Customer | undefined; isLoading: boolean; error: unknown } {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', 'detail', type, id],
    queryFn: () => customerAPI.getById(id!, type),
    enabled: id != null,
    staleTime: STALE_DETAIL_MS,
  });
  return { data, isLoading, error };
}

/**
 * Customer ↔ branch visibility links for the picker's branch section. Returns
 * the customer's current links plus the branch option set. Loaded lazily when
 * a customer is selected. Short staleTime — links change rarely during a shift
 * but should reflect a peer's edit reasonably quickly.
 */
export function useCustomerBranchLinks(
  id: number | null,
  type: CustomerType,
): { data: CustomerBranchLinks | undefined; isLoading: boolean; error: unknown } {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', 'branches', type, id],
    queryFn: () => customerAPI.getBranchLinks(id!, type),
    enabled: id != null,
    staleTime: STALE_DETAIL_MS,
  });
  return { data, isLoading, error };
}

// Lookups for the customer form. Categories/countries change very rarely, so
// cache them for the whole session.
const STALE_LOOKUP_MS = 60 * 60 * 1000; // 1 hour

export function useVendorCategories(enabled = true): { data: VendorCategory[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'categories'],
    queryFn: () => customerAPI.listCategories(),
    staleTime: STALE_LOOKUP_MS,
    enabled,
  });
  return { data: data?.data ?? [], isLoading };
}

/**
 * Countries for the address form. Also carries the tenant's default country —
 * every tenant DB holds exactly one mstr_Country row (its own), so a new
 * address should land on it instead of "— None —".
 */
export function useCountries(enabled = true): {
  data: Country[];
  defaultCountryId: number | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'countries'],
    queryFn: () => customerAPI.listCountries(),
    staleTime: STALE_LOOKUP_MS,
    enabled,
  });
  return {
    data: data?.data ?? [],
    defaultCountryId: data?.default_country_id ?? null,
    isLoading,
  };
}

/**
 * GL_Governorate options — المدينة (city) on the Saudi National Address
 * layout. Scoped to the address's country so a multi-country tenant doesn't
 * offer Riyadh zones under Kuwait.
 */
export function useGovernorates(
  countryId: number | null,
  enabled = true,
): { data: Governorate[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'governorates', countryId],
    queryFn: () => customerAPI.listGovernorates(countryId),
    staleTime: STALE_LOOKUP_MS,
    enabled,
  });
  return { data: data?.data ?? [], isLoading };
}

/**
 * GL_Area options under one city — الحي (district). Fetched per city rather
 * than filtering one global list client-side: flowfresh alone carries 398
 * areas against a server cap of 500, so the unscoped list is close to
 * truncating.
 */
export function useDistricts(
  governorateId: number | null,
  enabled = true,
): { data: Area[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'districts', governorateId],
    queryFn: () => customerAPI.listAreas(undefined, governorateId),
    staleTime: STALE_LOOKUP_MS,
    enabled: enabled && governorateId != null,
  });
  return { data: data?.data ?? [], isLoading };
}

/**
 * Subsidiary branch options for the customer form's branch-visibility picker
 * (works in add mode, where no customer id exists yet). Only fetched when the
 * branch-link feature is enabled.
 */
export function useBranchOptions(enabled = true): { data: SubsidiaryBranchOption[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'branch-options'],
    queryFn: () => customerAPI.listBranches(),
    staleTime: STALE_LOOKUP_MS,
    enabled,
  });
  return { data: data?.branches ?? [], isLoading };
}
